import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRuntime } from '@/app/providers/runtime-provider';
import { useNotify } from '@/shared/hooks/use-notify';
import { useRefreshSession } from '@/shared/hooks/use-refresh-session';
import { removeFragments } from './cache';
import { RealtimeContext } from './context';

/**
 * The realtime events that move the user (docs/plan/12-frontend.md §5), handled inside the router
 * because they navigate:
 * - access to the space or the rooms the person has open was revoked: a plain-language toast, back
 *   to the project list, and the cache of that space is removed;
 * - access to the organisation was revoked: the memberships are reloaded, and if the person is
 *   no longer a member of the open organisation they leave it.
 * (`session.revoked` is handled by the provider: it follows the same path as a 401.)
 */
export function RealtimeRouteEffects() {
  const context = useContext(RealtimeContext);
  const client = context?.client ?? null;
  const registry = context?.registry ?? null;
  const { t } = useTranslation('web');
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sessionStore } = useRuntime();
  const refreshSession = useRefreshSession();
  const { orgId } = useParams({ strict: false });

  useEffect(() => {
    if (!client || !registry) return;
    return client.on('access.revoked', ({ data }) => {
      if (data.scope === 'organization') {
        void (async () => {
          try {
            await refreshSession();
          } catch {
            return; // REST is the truth: the next successful /me tells the same
          }
          const stillMember =
            orgId === undefined ||
            (sessionStore.getState().me?.memberships.some((m) => m.organizationId === orgId) ??
              false);
          if (stillMember) return;
          notify({ intent: 'warning', message: t('realtime.accessRevoked.organization') });
          await navigate({ to: '/' });
          removeFragments(queryClient, [`/orgs/${orgId}/`]);
        })();
        return;
      }

      const { rooms, spaceIds } = registry.revokedBy(data);
      if (rooms.length === 0) return; // not where the person is: nothing on screen to leave
      const fragments = [...registry.fragments(rooms), ...spaceIds.map((id) => `/spaces/${id}/`)];
      notify({ intent: 'warning', message: t('realtime.accessRevoked.space') });
      const leave = orgId
        ? navigate({ to: '/o/$orgId/projects', params: { orgId } })
        : navigate({ to: '/' });
      // The queries are removed once the screens that read them are gone.
      void leave.finally(() => {
        removeFragments(queryClient, fragments);
      });
    });
  }, [client, registry, orgId, navigate, notify, queryClient, refreshSession, sessionStore, t]);

  return null;
}
